import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { ShieldCheck, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * AVG-consent gate. Blocks every coachee route with a full-screen modal
 * until the coachee has explicitly accepted the privacy statement.
 *
 * Existing coachees (created before the AVG rollout) are auto-accepted in
 * the backend migration, so they will sail through. Only NEW coachees see
 * this gate at first login.
 */
export default function ConsentGate({ children }) {
  // state: 'loading' | 'granted' | 'needed'
  const [state, setState] = useState("loading");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/consent");
        setState(data?.privacy_consent_at ? "granted" : "needed");
      } catch (err) {
        // If we can't tell, default to "granted" so we don't block on
        // transient errors. The next reload will retry.
        console.warn("Privacy consent check failed:", err?.message || err);
        setState("granted");
      }
    })();
  }, []);

  const handleAccept = async () => {
    if (!accepted) {
      setError("Vink eerst de bevestiging aan om door te gaan.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/me/consent", { accepted: true });
      setState("granted");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--c-bg)]">
        <div className="text-sm text-zinc-500">Laden...</div>
      </div>
    );
  }

  if (state === "needed") {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-900/60 flex items-center justify-center p-4" data-testid="consent-gate">
        <div className="bg-white max-w-xl w-full rounded-sm shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start gap-3 mb-4">
            <ShieldCheck size={32} weight="duotone" className="text-[#1E88E5] shrink-0" />
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Privacyverklaring</h2>
              <p className="text-sm text-zinc-500 mt-1">Akkoord vereist om de app te gebruiken.</p>
            </div>
          </div>

          <div className="text-sm text-zinc-700 leading-relaxed space-y-3">
            <p>
              Voordat je gebruik kunt maken van Werk Dat Past Online, vragen we je akkoord met onze
              verwerking van jouw persoonsgegevens. Wij verwerken onder andere je CV, testresultaten en
              AI-gespreksverlopen om je work coach te ondersteunen bij je begeleiding.
            </p>
            <p>
              Lees eerst de volledige verklaring — hierin staat o.a. welke gegevens we verwerken, met
              welke sub-verwerkers we werken (Anthropic Claude), hoe lang we bewaren, en welke rechten
              je hebt (inzage, rectificatie, verwijdering, etc.).
            </p>
            <p>
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium underline"
                data-testid="consent-gate-read-link"
              >
                Lees de privacyverklaring <ArrowSquareOut size={14} />
              </Link>
            </p>
          </div>

          <label className="flex items-start gap-3 mt-5 border-2 border-zinc-200 hover:border-zinc-400 transition-colors rounded-sm p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="consent-gate-checkbox"
              className="mt-1 h-4 w-4 accent-zinc-900"
            />
            <div className="text-sm text-zinc-800">
              Ik heb de privacyverklaring gelezen en ga akkoord met de verwerking van mijn persoonsgegevens
              zoals daarin beschreven.
            </div>
          </label>

          {error && (
            <div className="mt-3 text-sm text-[#E53935]" data-testid="consent-gate-error">{error}</div>
          )}

          <button
            type="button"
            onClick={handleAccept}
            disabled={submitting || !accepted}
            data-testid="consent-gate-accept-btn"
            className="mt-5 w-full bg-zinc-900 text-white py-3 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-300 disabled:cursor-not-allowed"
          >
            {submitting ? "Bezig..." : "Akkoord — ga door"}
          </button>
        </div>
      </div>
    );
  }

  return children;
}
